import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import PerFlexRow from '../FlexRow'
import PerText from '../Text'
import { Currency } from '../../../types'
import { useAmountAndCurrency } from '../../../shared/useAmountAndCurrency'
import { useCurrency } from '../../../context/currency-context'
import { round2digits } from '../../../modules/appraisal/appraisal-flow/meeting/util/paymentsUtil'

interface Props {
  achievedTargetBonus?: number
  oneOffPayment?: number
  targetCorrectionPayment?: number
  manualDeduction?: number
  hasMonetaryTargets: boolean
  formatCurrencyCallback: (amount: number) => string
  unknownCurrenciesForConversion?: Currency[]
}

export const PaymentFooterTemplate: FC<Props> = ({
  achievedTargetBonus,
  oneOffPayment,
  targetCorrectionPayment,
  manualDeduction,
  hasMonetaryTargets,
  formatCurrencyCallback,
  unknownCurrenciesForConversion = [],
}) => {
  const [t] = useTranslation()
  const { calculateAndFormatNewTargetAmount } = useAmountAndCurrency()
  const { currency } = useCurrency()

  const toPay = round2digits(
    (achievedTargetBonus ? achievedTargetBonus : 0) +
      (oneOffPayment ? oneOffPayment : 0) +
      (targetCorrectionPayment ? targetCorrectionPayment : 0) -
      (manualDeduction ? manualDeduction : 0),
  )
  const hasTargetCorrectionPayment: boolean = !!targetCorrectionPayment && targetCorrectionPayment > 0
  const hasManualDeduction: boolean = !!manualDeduction && manualDeduction > 0
  const hasOneOffPayment: boolean = !!oneOffPayment && oneOffPayment > 0

  return (
    <>
      <PerFlexRow align="middle">
        {hasMonetaryTargets && (
          <PerFlexRow mr="xxxl">
            <div>
              <PerText appearance="footer-label-small">
                <span data-e2e-payment-footer-achieved-target-label>{t('paymentFooter.targetValue')}</span>
              </PerText>
              {hasManualDeduction && <PerText appearance="footer-label-small">{t('paymentFooter.manualDeduction')}</PerText>}
            </div>
            <div>
              {hasMonetaryTargets && (
                <PerText
                  appearance="footer-input-small"
                  color={!!unknownCurrenciesForConversion.length ? 'error' : 'primary'}
                  tooltip={
                    unknownCurrenciesForConversion.length > 0
                      ? t('currencyConversion.tooltips.unknownConversionRate', {
                          currentContractCurrency: currency,
                          unknownCurrencies: unknownCurrenciesForConversion.join(', '),
                        })
                      : undefined
                  }
                >
                  <span data-e2e-payment-footer-achieved-target-amount>
                    {!!unknownCurrenciesForConversion.length
                      ? calculateAndFormatNewTargetAmount(0, 'EUR')
                      : formatCurrencyCallback(achievedTargetBonus ? achievedTargetBonus : 0)}
                  </span>
                </PerText>
              )}
              {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
              {hasManualDeduction && <PerText appearance="footer-input-small">- {formatCurrencyCallback(manualDeduction!)}</PerText>}
            </div>
          </PerFlexRow>
        )}
        {hasOneOffPayment && (
          <PerFlexRow mr="xxxl">
            <PerText appearance="footer-label-small">{t('paymentFooter.oneOff')}</PerText>
            {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
            <PerText appearance="footer-input-small">{formatCurrencyCallback(oneOffPayment!)}</PerText>
          </PerFlexRow>
        )}
        {hasTargetCorrectionPayment && (
          <PerFlexRow>
            <PerText appearance="footer-label-small">{t('paymentFooter.targetCorrection')}</PerText>
            {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
            <PerText appearance="footer-input-small">{formatCurrencyCallback(targetCorrectionPayment!)}</PerText>
          </PerFlexRow>
        )}
      </PerFlexRow>

      <PerFlexRow>
        <PerText appearance="footer-label">{t('pastTargetsFooter.toBePaid')}</PerText>
        <PerText
          appearance="footer-input"
          color={toPay < 0 || unknownCurrenciesForConversion.length > 0 ? 'error' : 'primary'}
          tooltip={
            unknownCurrenciesForConversion.length > 0
              ? t('currencyConversion.tooltips.unknownConversionRate', {
                  currentContractCurrency: currency,
                  unknownCurrencies: unknownCurrenciesForConversion.join(', '),
                })
              : undefined
          }
        >
          {!!unknownCurrenciesForConversion.length ? calculateAndFormatNewTargetAmount(0, 'EUR') : formatCurrencyCallback(toPay)}
        </PerText>
      </PerFlexRow>
    </>
  )
}
